import { Box, Stack, Typography, styled } from '@mui/material'
import NavigateNextRoundedIcon from '@mui/icons-material/NavigateNextRounded'
import { ReactNode, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router'
import { type TypographyProps } from '@mui/material/Typography'

export interface AccountHeaderProps {
  title: string
  titleActions?: ReactNode
  titlePrefix?: ReactNode
  breadcrumbs?: Array<{ title: string, url: string }>
  actions?: ReactNode
}

const AccountHeaderTitle = styled((props: Omit<TypographyProps, 'variant'>) => (
  <Typography variant="h1" {...props} />
))({
  fontFamily: 'Poppins, Roboto, sans-serif',
  fontSize: '1.8rem',
  fontWeight: 400,
  lineHeight: 'normal',
})

const AccountHeaderBreadcrumb = styled((props: Omit<TypographyProps<'span'>, 'component' | 'variant'>) => (
  <Typography variant="h1" component="span" {...props} />
))({
  fontFamily: 'Poppins, Roboto, sans-serif',
  fontSize: '1.8rem',
  fontWeight: 400,
  lineHeight: 'normal',
  color: '#999',
})

const AccountHeaderBreadcrumbLink = styled(Link)({
  color: '#999',
})

export default function AccountHeader(props: AccountHeaderProps) {
  const renderBreadcrumb = useCallback((breadcrumb: { title: string, url: string }) => {
    return <AccountHeaderBreadcrumb
      key={breadcrumb.title}
    >
      <AccountHeaderBreadcrumbLink to={breadcrumb.url}>{breadcrumb.title}</AccountHeaderBreadcrumbLink>
    </AccountHeaderBreadcrumb>
  }, [])

  return <Stack>
    <Helmet>
      <title>{props.title}</title>
    </Helmet>

    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: {
          xs: 'flex-start',
          sm: 'center',
        },
        marginBottom: 2,
        flexWrap: 'wrap',
        columnGap: 2,
        rowGap: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          flex: {
            xs: '1 1 100%',
            sm: '1 1 auto',
          },
          minWidth: 0,
        }}
      >
        {props.breadcrumbs && props.breadcrumbs.length > 0 ? <Stack
          direction="row"
          divider={<NavigateNextRoundedIcon
            sx={{
              color: '#999',
            }}
          />}
          sx={{
            alignItems: 'center',
            columnGap: 1,
            flexWrap: 'wrap',
          }}
        >
          {props.breadcrumbs.map(renderBreadcrumb)}
        </Stack> : null}
        {props.breadcrumbs && props.breadcrumbs.length > 0 ? <NavigateNextRoundedIcon
          sx={{
            color: '#999',
          }}
        /> : null}
        {props.titlePrefix}
        <AccountHeaderTitle>{props.title}</AccountHeaderTitle>
        {props.titleActions}
      </Stack>
      {props.actions ? <Box
        component="div"
        sx={{
          flex: {
            xs: '1 1 100%',
            sm: '0 0 auto',
          },
          display: 'flex',
          justifyContent: {
            xs: 'flex-start',
            sm: 'flex-end',
          },
        }}
      >
        {props.actions}
      </Box> : null}
    </Stack>
  </Stack>
}
